import { BufferGeometry, Camera, Mesh, Texture, Vector3, WebGLRenderer } from 'three';

export type TileCoord = { z: number; x: number; y: number };
export type TileKey = string;

export type Viewport = { width: number; height: number; pixelRatio: number };

export type TileDesire = {
  coord: TileCoord;
  fallbackChain: TileCoord[]; // e.g. [parent, grandparent, ... root]
};

export type WorldQuad = {
  // Order matters: these should form a non-self-intersecting quad.
  // Typical convention: a=SW, b=SE, c=NE, d=NW (or similar).
  a: Vector3;
  b: Vector3;
  c: Vector3;
  d: Vector3;
};

/**
 * WorldMapping is the “bridge” between XYZ tiles and your js world.
 *
 * It owns:
 * - how a tile becomes geometry in world space (flat plane vs terrain drape)
 * - how textures are created and applied
 * - how to estimate screen size (for LOD)
 * - how to dispose tile resources
 *
 * This keeps OrthoTileAsset clean and lets you swap mappings (flat/terrain/custom).
 */
export interface WorldMapping {
  /**
   * Create geometry for this tile in world space.
   * Flat plane: a quad.
   * Terrain: a grid with sampled heights.
   */
  tileToWorldGeometry(input: { coord: TileCoord }): BufferGeometry;

  /**
   * Optional fast path for LOD estimation:
   * Return 4-8 representative world points for this tile (corners usually).
   *
   * Use this instead of building geometry just to estimate size.
   */
  getTileWorldCorners(coord: TileCoord): Vector3[];

  /**
   * Estimate the on-screen pixel size of a tile using representative world points.
   * (Typically bounding box size in screen space.)
   */
  estimateScreenPx(cornersWorld: Vector3[], camera: Camera, renderer: WebGLRenderer): number;

  // -------------------------
  // Mesh/material/texture
  // -------------------------

  /**
   * Create a tile mesh using your preferred material setup (Basic/Standard/etc).
   * Texture is applied later when loaded.
   */
  createTileMesh(geometry: BufferGeometry): Mesh;

  /**
   * Apply the texture to the tile mesh.
   * This is where you decide:
   * - which material slot to set (map/emissiveMap/etc)
   * - opacity/transparent
   * - polygonOffset to avoid z-fighting
   * - UV mapping assumptions
   */
  applyTexture(mesh: Mesh, texture: Texture): void;

  /**
   * Dispose resources for a tile mesh when evicted.
   * (Geometry + materials; texture is often handled by TileCache.)
   */
  disposeTileMesh(mesh: Mesh): void;
}

/**
 * Fetches and decodes the texture for one tile. Implemented by the XYZ TileLoader and the COG loader.
 * Must reject with an AbortError when the signal is aborted, so the tile stays retryable.
 */
export interface TileTextureLoader {
  loadTileTexture(coord: TileCoord, signal?: AbortSignal): Promise<Texture>;
}

export function tileKey(c: TileCoord): TileKey {
  return `${c.z}/${c.x}/${c.y}`;
}
